import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { ChevronRight, CheckCircle2, Home, MapPin, Shield, AlertCircle, Upload, DollarSign, Users, Key, Bed, Bath, Ruler, Calendar, Phone, Mail, BarChart3, FileText, Image as ImageIcon, Zap } from 'lucide-react';

interface PropertyFormData {
  // Step 1: Basic Details
  address: string;
  postcode: string;
  city: string;
  country: string;
  propertyType: 'House' | 'Apartment' | 'Commercial' | 'Land' | '';
  yearBuilt: string;
  squareFeet: string;
  bedrooms: string;
  bathrooms: string;
  
  // Step 2: Owner Information
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  ownerAddress: string;
  
  // Step 3: Financial Details
  valuation: string;
  monthlyRent: string;
  mortgageAmount: string;
  monthlyMortgage: string;
  propertyTaxes: string;
  
  // Step 4: Documents & Compliance
  propertyDeedFile: File | null;
  insuranceDocFile: File | null;
  epcFile: File | null;
}

const AddPropertyPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<PropertyFormData>({
    address: '',
    postcode: '',
    city: '',
    country: 'United Kingdom',
    propertyType: '',
    yearBuilt: '',
    squareFeet: '',
    bedrooms: '',
    bathrooms: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    ownerAddress: '',
    valuation: '',
    monthlyRent: '',
    mortgageAmount: '',
    monthlyMortgage: '',
    propertyTaxes: '',
    propertyDeedFile: null,
    insuranceDocFile: null,
    epcFile: null,
  });

  const steps = [
    { number: 1, title: 'Property Details', description: 'Location & Specifications' },
    { number: 2, title: 'Owner Information', description: 'Contact Details' },
    { number: 3, title: 'Financial Details', description: 'Valuation & Rent' },
    { number: 4, title: 'Documents', description: 'Compliance & Verification' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({
        ...formData,
        [fieldName]: file,
      });
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.address && formData.postcode && formData.city && 
                 formData.propertyType && formData.bedrooms && formData.bathrooms);
      case 2:
        return !!(formData.ownerName && formData.ownerEmail && formData.ownerPhone);
      case 3:
        return !!(formData.valuation && formData.monthlyRent);
      case 4:
        return !!(formData.propertyDeedFile && formData.insuranceDocFile);
      default:
        return false;
    }
  };

  const handleSubmit = () => {
    if (validateStep(4)) {
      console.log('Property submitted:', formData);
      alert('Property added successfully!');
      navigate('/admin/properties');
    }
  };

  return (
    <>
      <Helmet>
        <title>Add New Property - VireWire Admin</title>
        <meta name="description" content="Add a new property to the VireWire platform" />
      </Helmet>

      <AdminLayout>
        <div className="p-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Home className="w-7 h-7 text-teal-600" />
                </div>
                Add New Property
              </h1>
              <p className="text-gray-600 text-lg">Register a new property portfolio property in VireWire</p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-12 bg-white border border-gray-200 rounded-xl p-8">
              <div className="flex items-center justify-between">
                {steps.map((step, idx) => (
                  <div key={step.number} className="flex items-center flex-1">
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                        currentStep > step.number
                          ? 'bg-green-600 text-white'
                          : currentStep === step.number
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {currentStep > step.number ? (
                          <CheckCircle2 className="w-6 h-6" />
                        ) : (
                          step.number
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{step.title}</p>
                        <p className="text-xs text-gray-600">{step.description}</p>
                      </div>
                    </div>
                    {idx < steps.length - 1 && (
                      <div className={`w-12 h-1 mx-4 rounded-full transition-all ${
                        currentStep > step.number ? 'bg-green-600' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Form Content */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-10">
              {/* Step 1: Property Details */}
              {currentStep === 1 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Home className="w-6 h-6 text-teal-600" />
                      </div>
                      Property Location & Details
                    </h2>
                    <p className="text-gray-600 ml-13">Complete basic property information</p>
                  </div>

                  {/* Location Information Card */}
                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                      <MapPin className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-bold text-gray-900">Location Details</h3>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Street Address</label>
                      <input
                        type="text"
                        name="address"
                        placeholder="e.g. 123 Oxford Street"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Postcode</label>
                        <input
                          type="text"
                          name="postcode"
                          placeholder="SW1A 1AA"
                          value={formData.postcode}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">City</label>
                        <input
                          type="text"
                          name="city"
                          placeholder="London"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Country</label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Property Type Card */}
                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                      <Home className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-bold text-gray-900">What type of property is this?</h3>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      {['House', 'Apartment', 'Commercial', 'Land'].map((type) => (
                        <button
                          key={type}
                          onClick={() => setFormData({ ...formData, propertyType: type as PropertyFormData['propertyType'] })}
                          className={`p-4 rounded-lg border-2 transition-all font-bold text-center ${
                            formData.propertyType === type
                              ? 'border-teal-600 bg-teal-50 text-teal-900 shadow-md'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:bg-teal-50'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Property Specifications Card */}
                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                      <Ruler className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-bold text-gray-900">Property Specifications</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          <Calendar className="w-4 h-4 inline mr-2" />
                          Year Built
                        </label>
                        <input
                          type="number"
                          name="yearBuilt"
                          placeholder="2015"
                          value={formData.yearBuilt}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          <Ruler className="w-4 h-4 inline mr-2" />
                          Square Feet
                        </label>
                        <input
                          type="text"
                          name="squareFeet"
                          placeholder="1,850"
                          value={formData.squareFeet}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          <Bed className="w-4 h-4 inline mr-2" />
                          Bedrooms
                        </label>
                        <input
                          type="number"
                          name="bedrooms"
                          placeholder="3"
                          value={formData.bedrooms}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          <Bath className="w-4 h-4 inline mr-2" />
                          Bathrooms
                        </label>
                        <input
                          type="number"
                          name="bathrooms"
                          placeholder="2"
                          value={formData.bathrooms}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-xl p-6">
                    <div className="flex gap-4">
                      <AlertCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-teal-900 mb-1">Accurate property specifications required</p>
                        <p className="text-sm text-teal-800">
                          Providing accurate information helps in proper valuation and tenant matching. All details will be verified during onboarding.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Owner Information */}
              {currentStep === 2 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-teal-600" />
                      </div>
                      Owner Information
                    </h2>
                    <p className="text-gray-600 ml-13">Provide property owner contact details</p>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                      <Users className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-bold text-gray-900">Contact Information</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Full Name</label>
                        <input
                          type="text"
                          name="ownerName"
                          placeholder="e.g. John Smith"
                          value={formData.ownerName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          <Mail className="w-4 h-4 inline mr-2" />
                          Email Address
                        </label>
                        <input
                          type="email"
                          name="ownerEmail"
                          placeholder="john.smith@example.com"
                          value={formData.ownerEmail}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-2" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="ownerPhone"
                        placeholder="e.g. 020 7946 0958"
                        value={formData.ownerPhone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <MapPin className="w-4 h-4 inline mr-2" />
                        Correspondence Address (Optional)
                      </label>
                      <input
                        type="text"
                        name="ownerAddress"
                        placeholder="Optional owner correspondence address"
                        value={formData.ownerAddress}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex gap-4">
                      <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-blue-900 mb-1">Contact information is important</p>
                        <p className="text-sm text-blue-800">
                          We'll use these details to contact the owner for verification and important property updates.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Financial Details */}
              {currentStep === 3 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-teal-600" />
                      </div>
                      Financial Details
                    </h2>
                    <p className="text-gray-600 ml-13">Enter valuation and rental information</p>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                      <DollarSign className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-bold text-gray-900">Core Financial Information</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Property Valuation</label>
                        <div className="relative">
                          <span className="absolute left-4 top-3 text-gray-600 font-bold">$</span>
                          <input
                            type="text"
                            name="valuation"
                            placeholder="850000"
                            value={formData.valuation}
                            onChange={handleInputChange}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Monthly Rent Income</label>
                        <div className="relative">
                          <span className="absolute left-4 top-3 text-gray-600 font-bold">$</span>
                          <input
                            type="text"
                            name="monthlyRent"
                            placeholder="3200"
                            value={formData.monthlyRent}
                            onChange={handleInputChange}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                      <BarChart3 className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-bold text-gray-900">Mortgage & Expenses (Optional)</h3>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Mortgage Amount</label>
                        <div className="relative">
                          <span className="absolute left-4 top-3 text-gray-600 font-bold">$</span>
                          <input
                            type="text"
                            name="mortgageAmount"
                            placeholder="500000"
                            value={formData.mortgageAmount}
                            onChange={handleInputChange}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Monthly Mortgage</label>
                        <div className="relative">
                          <span className="absolute left-4 top-3 text-gray-600 font-bold">$</span>
                          <input
                            type="text"
                            name="monthlyMortgage"
                            placeholder="1500"
                            value={formData.monthlyMortgage}
                            onChange={handleInputChange}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Annual Property Taxes</label>
                      <div className="relative">
                        <span className="absolute left-4 top-3 text-gray-600 font-bold">$</span>
                        <input
                          type="text"
                          name="propertyTaxes"
                          placeholder="1500"
                          value={formData.propertyTaxes}
                          onChange={handleInputChange}
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-200"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                    <div className="flex gap-4">
                      <AlertCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-green-900 mb-1">Use realistic figures</p>
                        <p className="text-sm text-green-800">
                          Providing accurate financial information ensures proper property valuations and helps attract suitable bookings.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Documents & Compliance */}
              {currentStep === 4 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                        <Shield className="w-6 h-6 text-teal-600" />
                      </div>
                      Documents & Compliance
                    </h2>
                    <p className="text-gray-600 ml-13">Upload required verification documents</p>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 space-y-6">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                      <FileText className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-bold text-gray-900">Required Documents</h3>
                    </div>

                    {/* Property Deed Upload */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="text-red-600">*</span> Property Deed
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-teal-600 hover:bg-teal-50 transition-all cursor-pointer group">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'propertyDeedFile')}
                          className="hidden"
                          id="propertyDeedInput"
                        />
                        <label htmlFor="propertyDeedInput" className="cursor-pointer block">
                          <Upload className="w-12 h-12 text-gray-400 group-hover:text-teal-600 mx-auto mb-4" />
                          <p className="font-bold text-gray-900 mb-2">Drag and drop or click</p>
                          <p className="text-xs text-gray-600 mb-4">PDF, DOC, or Image (Max 5MB)</p>
                          {formData.propertyDeedFile ? (
                            <div className="flex items-center justify-center gap-2">
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                              <span className="text-teal-600 font-bold">{formData.propertyDeedFile.name}</span>
                            </div>
                          ) : (
                            <span className="text-teal-600 hover:text-teal-700 font-bold">Click to upload file</span>
                          )}
                        </label>
                      </div>
                    </div>

                    {/* Buildings Insurance Upload */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="text-red-600">*</span> Buildings Insurance Certificate
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-teal-600 hover:bg-teal-50 transition-all cursor-pointer group">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'insuranceDocFile')}
                          className="hidden"
                          id="insuranceInput"
                        />
                        <label htmlFor="insuranceInput" className="cursor-pointer block">
                          <Zap className="w-12 h-12 text-gray-400 group-hover:text-teal-600 mx-auto mb-4" />
                          <p className="font-bold text-gray-900 mb-2">Drag and drop or click</p>
                          <p className="text-xs text-gray-600 mb-4">PDF, DOC, or Image (Max 5MB)</p>
                          {formData.insuranceDocFile ? (
                            <div className="flex items-center justify-center gap-2">
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                              <span className="text-teal-600 font-bold">{formData.insuranceDocFile.name}</span>
                            </div>
                          ) : (
                            <span className="text-teal-600 hover:text-teal-700 font-bold">Click to upload file</span>
                          )}
                        </label>
                      </div>
                    </div>

                    {/* Energy Performance Certificate Upload (Optional) */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        Energy Performance Certificate (Optional)
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-teal-600 hover:bg-teal-50 transition-all cursor-pointer group">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, 'epcFile')}
                          className="hidden"
                          id="epcInput"
                        />
                        <label htmlFor="epcInput" className="cursor-pointer block">
                          <ImageIcon className="w-12 h-12 text-gray-400 group-hover:text-teal-600 mx-auto mb-4" />
                          <p className="font-bold text-gray-900 mb-2">Drag and drop or click</p>
                          <p className="text-xs text-gray-600 mb-4">PDF, DOC, or Image (Max 5MB)</p>
                          {formData.epcFile ? (
                            <div className="flex items-center justify-center gap-2">
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                              <span className="text-teal-600 font-bold">{formData.epcFile.name}</span>
                            </div>
                          ) : (
                            <span className="text-teal-600 hover:text-teal-700 font-bold">Click to upload file</span>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
                    <div className="flex gap-4">
                      <Shield className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-purple-900 mb-1">Secure document verification</p>
                        <p className="text-sm text-purple-800">
                          All documents are encrypted and verified by our compliance team within 2-3 business days. Your data is always secure.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="mt-12 flex items-center justify-between border-t border-gray-200 pt-8">
                <button
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className={`flex items-center gap-2 px-6 py-3 font-bold text-sm transition-colors rounded-lg ${
                    currentStep === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                  Back
                </button>

                {currentStep < 4 ? (
                  <div className="flex gap-4">
                    <button
                      onClick={() => navigate('/admin/properties')}
                      className="px-6 py-3 font-bold text-sm text-gray-600 hover:bg-gray-100 transition-colors rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={!validateStep(currentStep)}
                      className={`px-8 py-3 font-bold text-sm flex items-center gap-2 rounded-lg transition-colors ${
                        !validateStep(currentStep)
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : 'bg-teal-600 text-white hover:bg-teal-700'
                      }`}
                    >
                      Next: {steps[currentStep].title}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-4">
                    <button
                      onClick={() => alert('Draft saved locally')}
                      className="px-8 py-3 font-bold text-sm text-gray-700 hover:bg-gray-100 transition-colors rounded-lg border border-gray-300"
                    >
                      Save Draft
                    </button>
                    <button
                      onClick={handleSubmit}
                      className="px-8 py-3 font-bold text-sm flex items-center gap-2 rounded-lg transition-colors bg-green-600 text-white hover:bg-green-700"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Add Property
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AddPropertyPage;
